
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SavedState } from '@/components/team-details/types/selector-types';

/**
 * Common query function to fetch saved states metadata from Supabase
 * Only fetches essential fields to improve performance
 */
export const fetchSavedStatesMetadata = async () => {
  const { data, error } = await supabase
    .from('saved_states')
    .select('id,name,country,league,year,created_at,display_order')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching saved states metadata:', error);
    throw error;
  }

  return data as SavedState[];
};

/**
 * Hook to retrieve and manage football saved states metadata with optimized caching
 * This version only fetches metadata for better performance on the homepage
 */
export const useFootballStates = () => {
  const { 
    data: savedStates, 
    isLoading, 
    refetch 
  } = useQuery({
    queryKey: ['saved-states-metadata'],
    queryFn: fetchSavedStatesMetadata,
    staleTime: 10 * 60 * 1000, // Data stays fresh for 10 minutes
    gcTime: 60 * 60 * 1000,    // Cache persists for 1 hour
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60 * 1000, // Poll for updates every 5 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 5000),
  });

  return { 
    savedStates, 
    isLoading, 
    refetch
  };
};
